import React, { useState, useRef, useEffect } from 'react';
import { Network as NetworkIcon, ZoomIn, ZoomOut, Maximize, RotateCcw, X } from 'lucide-react';
import cytoscape from 'cytoscape';
import coseBilkent from 'cytoscape-cose-bilkent';
import { transformDataToCytoscape, getHealthColor } from '../../utils/cytoscapeTransform';
import { createCytoscapeConfig } from '../../utils/cytoscapeConfig';
import { layoutConfig } from '../../utils/cytoscapeConfig';

// Register the layout
cytoscape.use(coseBilkent);

const DependencyNetworkGraph = ({ data }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const cyRef = useRef(null);
  const cyInstance = useRef(null);

  // Initialize Cytoscape when data changes
  useEffect(() => {
    if (!data || !Array.isArray(data) || data.length === 0 || !cyRef.current) {
      return;
    }

    try {
      // Transform data to Cytoscape format
      const { nodes, edges } = transformDataToCytoscape(data);

      if (nodes.length === 0) {
        return;
      }

      // Create Cytoscape configuration
      const config = createCytoscapeConfig(cyRef.current, nodes, edges);

      // Initialize Cytoscape instance
      const cy = cytoscape(config);

      // Node click handler
      cy.on('tap', 'node', (evt) => {
        const node = evt.target;
        setSelectedNode(node.data());
      });

      // Background click handler
      cy.on('tap', (evt) => {
        if (evt.target === cy) {
          setSelectedNode(null);
        }
      });

      cyInstance.current = cy;

      // Cleanup function
      return () => {
        if (cyInstance.current) {
          cyInstance.current.destroy();
          cyInstance.current = null;
        }
      };
    } catch (error) {
      console.error('[DependencyNetworkGraph] Failed to initialize Cytoscape:', error);
    }
  }, [data]);

  // Zoom controls
  const handleZoomIn = () => {
    if (cyInstance.current) {
      cyInstance.current.zoom(cyInstance.current.zoom() * 1.2);
      cyInstance.current.center();
    }
  };

  const handleZoomOut = () => {
    if (cyInstance.current) {
      cyInstance.current.zoom(cyInstance.current.zoom() * 0.8);
      cyInstance.current.center();
    }
  };

  const handleFitView = () => {
    if (cyInstance.current) {
      cyInstance.current.fit(null, 50);
    }
  };

  const handleResetLayout = () => {
    if (cyInstance.current) {
      cyInstance.current.layout(layoutConfig).run();
    }
  };

  // Handle empty or invalid data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="bg-dark-card rounded-lg border border-dark-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <NetworkIcon size={24} className="text-cyan-accent" />
            <h3 className="text-xl font-bold text-cyan-accent">Dependency Network Graph</h3>
          </div>
        </div>
        <div className="text-center text-gray-400 py-12">
          No dependency data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-dark-card rounded-lg border border-dark-border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <NetworkIcon size={24} className="text-cyan-accent" />
          <h3 className="text-xl font-bold text-cyan-accent">Dependency Network Graph</h3>
          <span className="text-sm text-gray-500">(Interactive visualization)</span>
        </div>
      </div>

      {/* Cytoscape Graph Container */}
      <div className="relative bg-black/40 rounded-lg border border-dark-border/60" style={{ height: '600px' }}>
        <div className="flex h-full">
          {/* Graph */}
          <div className={`${selectedNode ? 'flex-1' : 'w-full'} h-full transition-all`}>
            <div ref={cyRef} className="w-full h-full" />
          </div>

          {/* Node Details Side Panel */}
          {selectedNode && (
            <div className="w-80 bg-dark-border/20 border-l border-dark-border/60 p-4 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-cyan-accent">Node Details</h3>
                <button
                  onClick={() => setSelectedNode(null)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Label</div>
                  <div className="text-white font-semibold">{selectedNode.label || 'Unknown'}</div>
                </div>
                
                {selectedNode.description && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Description</div>
                    <div className="text-white text-sm">{selectedNode.description}</div>
                  </div>
                )}
                
                <div>
                  <div className="text-xs text-gray-500 mb-1">Type</div>
                  <div className="text-white">{selectedNode.type || 'N/A'}</div>
                </div>
                
                {selectedNode.direction && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Direction</div>
                    <div className="text-white capitalize">{selectedNode.direction}</div>
                  </div>
                )}
                
                {selectedNode.health && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Health Status</div>
                    <div className={`font-semibold ${
                      selectedNode.health === 'HEALTHY' ? 'text-green-500' :
                      selectedNode.health === 'DEGRADED' ? 'text-yellow-500' :
                      selectedNode.health === 'UNHEALTHY' ? 'text-red-500' : 'text-gray-500'
                    }`}>
                      {selectedNode.health}
                    </div>
                  </div>
                )}
                
                <div>
                  <div className="text-xs text-gray-500 mb-1">Health Score</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-dark-border rounded-full overflow-hidden">
                      <div 
                        className="h-full transition-all"
                        style={{ 
                          width: `${selectedNode.healthScore || 0}%`,
                          backgroundColor: getHealthColor(selectedNode.healthScore || 0)
                        }}
                      />
                    </div>
                    <span className="text-white font-semibold">{Math.round(selectedNode.healthScore || 0)}</span>
                  </div>
                </div>
                
                {selectedNode.isRoot && (
                  <div className="mt-4 p-3 bg-cyan-accent/10 border border-cyan-accent/30 rounded">
                    <div className="text-cyan-accent text-sm font-semibold">Main Change Node</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Zoom Controls */}
        <div className="absolute bottom-4 right-4 flex flex-col gap-2">
          <button
            onClick={handleZoomIn}
            className="p-2 rounded-lg bg-dark-card/90 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 transition-all backdrop-blur-sm"
            title="Zoom In"
          >
            <ZoomIn size={20} />
          </button>
          <button
            onClick={handleZoomOut}
            className="p-2 rounded-lg bg-dark-card/90 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 transition-all backdrop-blur-sm"
            title="Zoom Out"
          >
            <ZoomOut size={20} />
          </button>
          <button
            onClick={handleFitView}
            className="p-2 rounded-lg bg-dark-card/90 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 transition-all backdrop-blur-sm"
            title="Fit to View"
          >
            <Maximize size={20} />
          </button>
          <button
            onClick={handleResetLayout}
            className="p-2 rounded-lg bg-dark-card/90 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 transition-all backdrop-blur-sm"
            title="Reset Layout"
          >
            <RotateCcw size={20} />
          </button>
        </div>
      </div>

      {/* Legend and Statistics */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Node Color Legend */}
        <div className="bg-black/40 rounded-lg border border-dark-border/60 p-4">
          <h4 className="text-sm font-semibold text-gray-400 mb-3">Node Colors</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-[#00e5ff]" />
              <span className="text-sm text-gray-300">Main Change</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-[#b24bf3]" />
              <span className="text-sm text-gray-300">Upstream Dependency</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-[#ff6b35]" />
              <span className="text-sm text-gray-300">Downstream Dependency</span>
            </div>
          </div>
        </div>

        {/* Health Status Legend */}
        <div className="bg-black/40 rounded-lg border border-dark-border/60 p-4">
          <h4 className="text-sm font-semibold text-gray-400 mb-3">Health Status (Border Color)</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full border-4 border-[#10b981]" />
              <span className="text-sm text-gray-300">Healthy (80-100)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full border-4 border-[#eab308]" />
              <span className="text-sm text-gray-300">Degraded (60-79)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full border-4 border-[#f97316]" />
              <span className="text-sm text-gray-300">Warning (30-59)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full border-4 border-[#ef4444]" />
              <span className="text-sm text-gray-300">Critical (0-29)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="mt-4 bg-black/40 rounded-lg border border-dark-border/60 p-4">
        <h4 className="text-sm font-semibold text-gray-400 mb-3">Graph Statistics</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-2xl font-bold text-cyan-accent">
              {data.length}
            </div>
            <div className="text-xs text-gray-500">Total Changes</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-[#b24bf3]">
              {data.reduce((sum, item) => sum + (item.raw_data?.relationships?.upstream?.length || 0), 0)}
            </div>
            <div className="text-xs text-gray-500">Upstream Dependencies</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-[#ff6b35]">
              {data.reduce((sum, item) => sum + (item.raw_data?.relationships?.downstream?.length || 0), 0)}
            </div>
            <div className="text-xs text-gray-500">Downstream Dependencies</div>
          </div>
        </div>
        <div className="mt-3 text-xs text-gray-500 italic">
          Tip: Click nodes to view details, use controls to navigate, drag to pan
        </div>
      </div>
    </div>
  );
};

export default DependencyNetworkGraph;
